import {
  FaWhatsapp,
  FaLinkedinIn,
  FaFacebookF,
  FaInstagram,
  FaYoutube,
  FaTiktok,
  FaPhone,
  FaEnvelope,
  FaGlobe,
} from "react-icons/fa";
import { parseLibraryIcon } from "./libraryIcons";
import { isIconUrl } from "./cmsIconCatalog";

/**
 * Presets de redes: plataforma → icono de catálogo (react-icons vía clave ri:).
 * El Footer puede elegir cualquier icono del catálogo (ri / lucide / marca / URL subida categoría redes).
 */
export const SOCIAL_PLATFORMS = [
  { key: "whatsapp", label: "WhatsApp", Icon: FaWhatsapp, iconKey: "ri:FaWhatsapp" },
  { key: "linkedin", label: "LinkedIn", Icon: FaLinkedinIn, iconKey: "ri:FaLinkedinIn" },
  { key: "facebook", label: "Facebook", Icon: FaFacebookF, iconKey: "ri:FaFacebookF" },
  { key: "instagram", label: "Instagram", Icon: FaInstagram, iconKey: "ri:FaInstagram" },
  { key: "youtube", label: "YouTube", Icon: FaYoutube, iconKey: "ri:FaYoutube" },
  { key: "tiktok", label: "TikTok", Icon: FaTiktok, iconKey: "ri:FaTiktok" },
  { key: "email", label: "Correo", Icon: FaEnvelope, iconKey: "ri:FaEnvelope" },
  { key: "telefono", label: "Teléfono", Icon: FaPhone, iconKey: "ri:FaPhone" },
  { key: "web", label: "Sitio web", Icon: FaGlobe, iconKey: "ri:FaGlobe" },
];

export const SOCIAL_ICON_MAP = Object.fromEntries(
  SOCIAL_PLATFORMS.map((p) => [p.key, p.Icon])
);

export const SOCIAL_PRESET_OPTIONS = SOCIAL_PLATFORMS.map((p) => ({
  key: p.iconKey,
  platform: p.key,
  label: p.label,
  Component: p.Icon,
  classification: "redes",
}));

function platformMeta(key) {
  return SOCIAL_PLATFORMS.find((p) => p.key === key) || null;
}

function formatUrl(keyOrPlatform, raw) {
  let url = String(raw).trim();
  if (!url) return "";
  const platform = keyOrPlatform || "";
  if (
    (platform === "email" || String(platform).includes("Envelope") || String(platform).includes("Mail")) &&
    !url.startsWith("mailto:") &&
    !url.startsWith("http")
  ) {
    url = `mailto:${url}`;
  }
  if (
    (platform === "telefono" || String(platform).includes("Phone")) &&
    !url.startsWith("tel:") &&
    !url.startsWith("http")
  ) {
    url = `tel:${url.replace(/\s+/g, "")}`;
  }
  return url;
}

function newLinkId() {
  return `social-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function resolveIconComponent(iconKey, platformFallback) {
  if (iconKey) {
    const lib = parseLibraryIcon(iconKey);
    if (lib?.Component) return lib.Component;
    if (isIconUrl(iconKey)) return null;
  }
  const meta = platformMeta(platformFallback);
  return meta?.Icon || null;
}

/**
 * Publicación del enlace de footer (producto).
 * Ausente / desconocido → se trata como "todos" (compatibilidad).
 */
export const FOOTER_PUBLICATIONS = [
  { key: "todos", label: "Todos (Website + Academia)" },
  { key: "website", label: "Solo Website" },
  { key: "academia", label: "Solo Academia" },
];

export function normalizeFooterPublication(value) {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  if (v === "website" || v === "academia" || v === "todos") return v;
  return "todos";
}

/** ¿Mostrar este ítem en el producto actual? */
export function footerItemVisibleOn(publication, audience) {
  const pub = normalizeFooterPublication(publication);
  const aud = String(audience || "").trim().toLowerCase();
  if (aud !== "website" && aud !== "academia") return true;
  return pub === "todos" || pub === aud;
}

export function filterFooterItemsForAudience(items, audience) {
  if (!Array.isArray(items)) return [];
  return items.filter((row) => footerItemVisibleOn(row?.publication, audience));
}

/**
 * Lista editable del Footer.
 * Formato canónico: { id, label, url, icon, platform?, publication? }
 */
export function toSocialLinksList(input) {
  if (Array.isArray(input)) {
    return input
      .map((row, idx) => {
        const platform = String(row?.platform || row?.key || "").trim();
        const meta = platformMeta(platform);
        const url = String(row?.url || "").trim();
        const label = String(row?.label || meta?.label || platform || "Enlace").trim();
        const icon = String(row?.icon || meta?.iconKey || "").trim();
        if (!label && !url) return null;
        const item = {
          id: row?.id || `social-${idx}-${platform || "link"}`,
          label,
          url,
          icon: icon || meta?.iconKey || "ri:FaGlobe",
          publication: normalizeFooterPublication(row?.publication),
        };
        if (platform) item.platform = platform;
        return item;
      })
      .filter(Boolean);
  }

  if (input && typeof input === "object") {
    return SOCIAL_PLATFORMS.filter((p) => p.key !== "web")
      .map(({ key, label, iconKey }) => {
        const url = input[key];
        if (url == null || String(url).trim() === "") return null;
        return {
          id: newLinkId(),
          label,
          url: String(url).trim(),
          icon: iconKey,
          platform: key,
          publication: "todos",
        };
      })
      .filter(Boolean);
  }

  return [];
}

export function socialLinksToMap(list) {
  const map = {};
  for (const row of list || []) {
    const key = row?.platform || slugFromLabel(row?.label);
    if (!key || !row?.url) continue;
    map[key] = String(row.url).trim();
  }
  return map;
}

function slugFromLabel(label) {
  return String(label || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 32);
}

/**
 * Render público. Devuelve { key, label, url, icon, Icon, publication }.
 * @param {unknown} input
 * @param {{ audience?: "website" | "academia" }} [opts] — si se omite, no filtra (CMS / preview).
 */
export function getSocialLinks(input, opts = {}) {
  let list = toSocialLinksList(input);
  if (opts.audience) {
    list = filterFooterItemsForAudience(list, opts.audience);
  }
  return list
    .map(({ id, label, url, icon, platform, publication }, idx) => {
      const href = formatUrl(platform || icon, url);
      if (!href) return null;
      const Icon = resolveIconComponent(icon, platform);
      return {
        key: id || `${platform || "link"}-${idx}`,
        label: label || platform || "Enlace",
        url: href,
        icon: icon || null,
        Icon,
        publication,
      };
    })
    .filter(Boolean);
}

export function createEmptySocialLink() {
  const first = SOCIAL_PLATFORMS[0];
  return {
    id: newLinkId(),
    label: first.label,
    url: "",
    icon: first.iconKey,
    platform: first.key,
    publication: "todos",
  };
}

export function applyPlatformPreset(platformKey) {
  const meta = platformMeta(platformKey);
  if (!meta) return {};
  return {
    platform: meta.key,
    label: meta.label,
    icon: meta.iconKey,
  };
}
